import { Check } from "lucide-react";
import type { WeekActivities } from "../../../types/gpSummary";
import Card from "./Card";

interface ActivitiesCardProps {
  weeklyActivities: WeekActivities[];
  completedWeek: number;
}

export default function ActivitiesCard({ weeklyActivities, completedWeek }: ActivitiesCardProps) {
  const totalDone = weeklyActivities.reduce((sum, w) => sum + w.completedCount, 0);
  const totalAll = weeklyActivities.reduce((sum, w) => sum + w.totalCount, 0);
  const pct = totalAll > 0 ? Math.round((totalDone / totalAll) * 100) : 0;

  if (weeklyActivities.length === 0 || totalAll === 0) {
    return (
      <Card title="Programme Activities">
        <p className="text-sm text-stone-400 italic">No activities recorded yet.</p>
      </Card>
    );
  }

  return (
    <Card title="Programme Activities">
      <div className="space-y-4">
        {/* Overall progress */}
        <div className="bg-white rounded-2xl p-5 border border-stone-100 flex items-center gap-5">
          <div className="relative w-16 h-16 shrink-0">
            <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
              <circle cx="18" cy="18" r="15.9" fill="none" stroke="#f5f5f4" strokeWidth="3" />
              <circle
                cx="18" cy="18" r="15.9" fill="none"
                stroke="#1E5E5B"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${pct} ${100 - pct}`}
              />
            </svg>
            <span className="absolute inset-0 flex items-center justify-center text-sm font-semibold text-stone-700">
              {pct}%
            </span>
          </div>
          <div>
            <p className="text-2xl font-bold text-stone-700">{totalDone}/{totalAll}</p>
            <p className="text-sm text-stone-400">activities completed across 6 weeks</p>
          </div>
        </div>

        {/* Compact week grid */}
        <div className="grid grid-cols-2 gap-3">
          {weeklyActivities.map((w) => {
            const done = w.completedCount;
            const total = w.totalCount;
            const complete = total > 0 && done === total;
            const notStarted = w.week > completedWeek && completedWeek > 0;

            return (
              <div
                key={w.week}
                className={`rounded-2xl p-4 border ${
                  notStarted
                    ? "bg-white border-stone-100 opacity-40"
                    : complete
                      ? "bg-[#e9f0dc] border-[#1E5E5B]/20"
                      : "bg-white border-stone-200"
                }`}
              >
                <div className="flex items-center justify-between mb-3">
                  <span className="text-sm font-semibold text-stone-700">Week {w.week}</span>
                  <span
                    className="text-xs font-medium px-2 py-0.5 rounded-full"
                    style={
                      complete
                        ? { background: "#1E5E5B", color: "#FFFFFF" }
                        : { background: "#f5f5f4", color: "#a8a29e" }
                    }
                  >
                    {done}/{total}
                  </span>
                </div>
                <div className="flex gap-1.5">
                  {w.activities.map((a) => (
                    <div
                      key={a.id}
                      title={a.title}
                      className={`w-7 h-7 rounded-full flex items-center justify-center ${
                        a.completed
                          ? "bg-[#1E5E5B]/90"
                          : "bg-stone-100 border border-stone-200"
                      }`}
                    >
                      {a.completed && <Check size={14} className="text-white" strokeWidth={2.5} />}
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </Card>
  );
}
